/**
 * @fileoverview User Preferences Service - Manages user preferences (theme, expiry, etc.)
 *
 * Syncs user preferences with the database, IndexedDB, and localStorage.
 * Preferences are persisted to user_profiles.preferences JSONB field.
 *
 * @module services/UserPreferencesService
 */

import { createLogger, isRecord } from '@xanectech/lib';
import { supabase } from '@xanectech/supabase-client';
import type { Json } from '@xanectech/supabase-client';
import type { UserPreferences } from '@xanectech/types';
import { updateUnlockExpiryWithPreference } from './UnlockPreferencesService';

const logger = createLogger('UserPreferencesService');

const STORAGE_KEY = 'xanectech_user_preferences';
const DB_NAME = 'XanecTechUserPreferences';
const DB_VERSION = 1;
const STORE_NAME = 'preferences';

interface StoredPreference {
  userId: string;
  preferences: UserPreferences;
  updatedAt: number;
}

/**
 * Initialize IndexedDB for user preferences
 */
async function initDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(new Error('Failed to open preferences DB'));
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'userId' });
      }
    };
  });
}

/**
 * Get user preferences from localStorage
 */
export function getPreferencesFromLocalStorage(
  userId: string,
): UserPreferences | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const parsed: unknown = JSON.parse(stored);
    if (isRecord(parsed) && parsed.userId === userId) {
      const data = parsed.data;
      return isRecord(data) ? (data as UserPreferences) : null;
    }
    return null;
  } catch (error) {
    logger.warn('Failed to read preferences from localStorage', {
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
      userId,
    });
    return null;
  }
}

/**
 * Set user preferences in localStorage
 */
export function setPreferencesInLocalStorage(
  userId: string,
  preferences: UserPreferences,
): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        userId,
        data: preferences,
        timestamp: Date.now(),
      }),
    );
  } catch (error) {
    logger.warn('Failed to write preferences to localStorage', {
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
      userId,
    });
  }
}

/**
 * Get user preferences from IndexedDB
 */
export async function getPreferencesFromIndexedDB(
  userId: string,
): Promise<UserPreferences | null> {
  try {
    const db = await initDB();
    return new Promise<UserPreferences | null>((resolve) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(userId);

      request.onsuccess = () => {
        db.close();
        const record = request.result as StoredPreference | undefined;
        resolve(record?.preferences || null);
      };

      request.onerror = () => {
        db.close();
        logger.warn('Failed to read from IndexedDB', { userId });
        resolve(null);
      };
    });
  } catch (error) {
    logger.warn('Failed to access IndexedDB', {
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
      userId,
    });
    return null;
  }
}

/**
 * Set user preferences in IndexedDB
 */
export async function setPreferencesInIndexedDB(
  userId: string,
  preferences: UserPreferences,
): Promise<void> {
  try {
    const db = await initDB();
    return new Promise<void>((resolve) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      const record: StoredPreference = {
        userId,
        preferences,
        updatedAt: Date.now(),
      };

      const request = store.put(record);

      request.onsuccess = () => {
        db.close();
        resolve();
      };

      request.onerror = () => {
        db.close();
        logger.warn('Failed to write to IndexedDB', { userId });
        resolve();
      };
    });
  } catch (error) {
    logger.warn('Failed to access IndexedDB', {
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
      userId,
    });
  }
}

/**
 * Fetch user preferences from database
 */
export async function fetchPreferencesFromDatabase(
  userId: string,
): Promise<UserPreferences | null> {
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('preferences')
      .eq('user_id', userId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No profile found
        return null;
      }
      throw error;
    }

    if (!isRecord(data)) {
      return null;
    }

    return isRecord(data.preferences)
      ? (data.preferences as UserPreferences)
      : null;
  } catch (error) {
    logger.error('Failed to fetch preferences from database', error as Error);
    return null;
  }
}

/**
 * Update user preferences in the database
 */
export async function updatePreferencesInDatabase(
  userId: string,
  preferences: UserPreferences,
): Promise<boolean> {
  try {
    const normalizedPreferences = JSON.parse(
      JSON.stringify(preferences),
    ) as Json;

    const { error } = await supabase
      .from('user_profiles')
      .update({ preferences: normalizedPreferences })
      .eq('user_id', userId);

    if (error) {
      logger.error('Failed to update preferences in database', error);
      return false;
    }

    logger.info('Preferences updated in database', { userId });
    return true;
  } catch (error) {
    logger.error('Error updating preferences in database', error as Error);
    return false;
  }
}

/**
 * Get or load user preferences with fallback chain:
 * 1. Try localStorage (fastest)
 * 2. Try IndexedDB (offline support)
 * 3. Fetch from database (source of truth)
 */
export async function getUserPreferences(
  userId: string,
): Promise<UserPreferences> {
  // Try localStorage first
  let preferences = getPreferencesFromLocalStorage(userId);
  if (preferences) {
    return preferences;
  }

  // Try IndexedDB
  preferences = await getPreferencesFromIndexedDB(userId);
  if (preferences) {
    setPreferencesInLocalStorage(userId, preferences); // Update localStorage
    return preferences;
  }

  // Fetch from database
  preferences = await fetchPreferencesFromDatabase(userId);
  if (preferences) {
    // Cache in both localStorage and IndexedDB
    setPreferencesInLocalStorage(userId, preferences);
    await setPreferencesInIndexedDB(userId, preferences);
    return preferences;
  }

  // Return defaults if nothing found
  return {
    expiry: 'one-hour',
    theme: 'system',
  };
}

/**
 * Set user preferences with synchronization:
 * 1. Update localStorage (immediate)
 * 2. Update IndexedDB (offline cache)
 * 3. Update database (persistent)
 * 4. Update active unlock record if expiry changed
 */
export async function setUserPreferences(
  userId: string,
  preferences: Partial<UserPreferences>,
): Promise<UserPreferences> {
  try {
    // Get current preferences
    const current = await getUserPreferences(userId);

    // Merge with new preferences
    const merged: UserPreferences = {
      ...current,
      ...preferences,
    };

    // Update localStorage immediately
    setPreferencesInLocalStorage(userId, merged);

    // Update IndexedDB asynchronously
    await setPreferencesInIndexedDB(userId, merged);

    // Update database
    const success = await updatePreferencesInDatabase(userId, merged);

    if (!success) {
      logger.warn('Database update failed, but local caches updated', {
        userId,
      });
    }

    // If expiry preference changed, update active unlock record
    if (preferences.expiry && preferences.expiry !== current.expiry) {
      try {
        await updateUnlockExpiryWithPreference(
          userId,
          preferences.expiry as string,
        );
      } catch (error) {
        logger.warn('Failed to update active unlock record', {
          error: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
          userId,
        });
      }
    }

    logger.info('User preferences updated', { userId, preferences: merged });
    return merged;
  } catch (error) {
    logger.error('Error setting user preferences', error as Error);
    throw error;
  }
}

/**
 * Clear all preference caches (on logout)
 */
export async function clearPreferenceCaches(userId: string): Promise<void> {
  try {
    // Clear localStorage
    localStorage.removeItem(STORAGE_KEY);

    // Clear IndexedDB
    const db = await initDB();
    return new Promise<void>((resolve) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(userId);

      request.onsuccess = () => {
        db.close();
        resolve();
      };

      request.onerror = () => {
        db.close();
        resolve();
      };
    });
  } catch (error) {
    logger.warn('Failed to clear preference caches', {
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
      userId,
    });
  }
}
